<template>
  <div>
    <SkinnyBanner page="product" />

    <Container
      v-if="productResponse"
      class="bg-surface-default-low pb-18 md:pb-48"
    >
      <Breadcrumb
        v-if="breadcrumbResponse"
        :breads="[
          ...breadcrumbResponse.breadcrumb,
          { label: productResponse.titles.raw, url: route.path },
        ]"
        class="bg-surface-default-low hidden md:block"
        trackingZone="product"
      />
      <div
        class="flex flex-wrap justify-between gap-x-8 pb-8 ps-24 md:hidden md:p-0 md:ps-0"
      >
        <BreadcrumbProductPage
          v-if="breadcrumbResponse"
          :breads="breadcrumbResponse.breadcrumb"
          class="bg-surface-default-low"
          trackingZone="product"
        />
      </div>
      <OutOfStock
        v-if="isOutOfStock"
        :alternative="outOfStockAlternative"
        :breadcrumb="breadcrumbResponse?.breadcrumb ?? []"
        class="ps-24 md:ps-0"
        :is-alternative-loading="outOfStockAlternativeLoading"
        :product="productResponse"
        :ratings="ratesResponse"
      />

      <div
        v-if="!isOutOfStock"
        class="mt-12 flex flex-col flex-wrap md:mt-32 md:flex-row md:flex-nowrap md:justify-evenly"
      >
        <div
          class="relative ml-0 mr-auto w-full max-w-full grow md:w-1/2 md:max-w-[min(33.33%,416px)] lg:max-w-[min(50%,560px)]"
        >
          <GalleryNew
            :images="productResponse.images"
            :product-type="productResponse.type"
            :review="reviewInCarouselResponse"
            should-preload-first-img
            :tracking-product-model="productResponse.model ?? ''"
          />
        </div>

        <TheMainContent
          class="w-full max-w-full grow px-24 md:w-2/3 md:basis-2/3 lg:w-1/2 lg:basis-1/2 lg:pe-0 lg:ps-64"
        >
          <div class="flex flex-col items-start md:flex-col-reverse">
            <div class="w-full">
              <TitleDesktop
                v-if="price"
                :aria-hidden="isMobile"
                class="hidden md:block"
                :price
                :product="productResponse"
                :rating="ratesResponse"
                :selected-mobile-plan
                :selected-offer
                :tracking="productTracking"
              />

              <TitleMobile
                v-if="price"
                :aria-hidden="!isMobile"
                class="md:hidden"
                :price
                :product="productResponse"
                :rating="ratesResponse"
                :selected-offer
                :tracking="productTracking"
              />
              <TitleMobileAtc
                v-if="price"
                ref="addToCartMobileButtonVisibilityBeacon"
                class="md:hidden"
                :price
                :product="productResponse"
                :rating="ratesResponse"
                :selected-mobile-plan
                :selected-offer
                :tracking="productTracking"
              />
            </div>
            <FlashSalesSticker
              class="my-8 w-full sm:mb-16 sm:mt-0 md:w-auto"
              :discount="flashSalesDiscount"
              :has-deals="productResponse.hasDeals"
            />
          </div>
          <RevDivider v-if="price" class="my-16 md:hidden" />
          <AdvertisedPayment :selected-offer />
          <ReassuranceItems
            v-if="productResponse && selectedOffer"
            :carrier="productResponse.carrier"
            class="mt-24"
            :shipping="selectedOffer.shipping"
            :show-express-replacement
            :show-obsolete-devices="productResponse.showObsoleteDevices"
            :show-verified-refurbished="
              productResponse.showVerifiedRefurbished && !showExpressReplacement
            "
            :warranty-delay="selectedOffer.defaultWarrantyDelay"
          />

          <VerifiedRefurbishedCarousel
            v-if="
              productResponse && shouldDisplayNewVerifiedRefurbishedCarousel
            "
            :category-id="productResponse.tracking.categoryId"
          />

          <SellerComment
            v-if="
              productResponse && selectedOffer?.sellerComment && hasNoVariants
            "
            :comment="selectedOffer.sellerComment"
          />

          <Seller
            v-if="productResponse && selectedOffer && hasNoVariants"
            :category="productResponse.type"
            :selected-offer
          />
        </TheMainContent>
      </div>
    </Container>

    <CheckpointsCarousel
      v-if="productResponse && !shouldDisplayNewVerifiedRefurbishedCarousel"
      :category-id="productResponse.tracking.categoryId"
      class="pb-48"
    />
    <RevContainer
      v-if="productResponse && selectedOffer && !isOutOfStock && price"
      class="bg-surface-default-low"
    >
      <CustomizationFunnelWrapper
        v-if="pickersResponse"
        :breadcrumb="breadcrumbResponse"
        :is-services-pickers-response-pending
        :pickers-response
        :pickers-services="servicesPickersResponse"
        :price
        :price-without-subsidies
        :price-without-trade-in
        :product="productResponse"
        :product-tracking
        :selected-mobile-plan
        :selected-offer
        :technical-specifications="technicalSpecificationsResponse"
      />
    </RevContainer>

    <RevContainer
      v-if="productResponse && !errorReco"
      class="mt-56"
      data-id="reco-section"
    >
      <Recommendations
        :product="productResponse"
        :selected-offer
        @bundle-add-to-cart="onBundleAddToCart"
        @recommendations-error="onRecommendationsError"
        @recommendations-loaded="onRecommendationsLoaded"
      />
    </RevContainer>

    <BlockSection
      v-if="cmsSections"
      class="mb-56"
      :section="cmsSections.section1"
    />

    <RevContainer v-if="productResponse && ratesResponse?.count" class="mb-56">
      <ReviewsBlock
        id="reviews"
        :ai-summary="reviewsAISummary"
        :ai-summary-pending="reviewsAiSummaryPending"
        :merchant-id="selectedOffer?.merchant?.merchantId"
        :merchant-url="selectedOffer?.merchant?.merchantUrl"
        :next-reviews-cursor="reviewsResponse?.nextCursor"
        :pending-summary="reviewsSummaryPending"
        :previous-reviews-cursor="reviewsResponse?.previousCursor"
        :product-id="productResponse.productId"
        :product-title="productResponse.titles.default"
        :rating="ratesResponse"
        :rating-summary="reviewsSummaryResponse"
        :reviews="reviews.list"
        :reviews-pending="reviews.isPending"
        :tracking-info="productTracking.product.value"
      />
    </RevContainer>

    <BlockSection
      v-if="cmsSections"
      class="mb-56"
      :section="cmsSections.section2"
    />

    <div class="bg-surface-default-hi">
      <RevContainer v-if="productResponse && seoLinksList">
        <SeoLinks class="my-32 md:my-72" :links="seoLinksList" />
      </RevContainer>

      <BlockSection v-if="cmsSections" :section="cmsSections.section3" />

      <BlockSection v-if="cmsSections" :section="cmsSections.section4" />

      <RevContainer>
        <SeoContent
          v-if="productResponse && technicalSpecificationsResponse"
          class="my-32 md:my-72"
          data-id="seo-technical-specs"
          :description="productResponse.seo.description ?? ''"
          :model="productResponse.model"
          :technical-specifications="technicalSpecificationsResponse"
          :title="productResponse.titles.raw"
        >
          <ScoreCard
            class="pb-24"
            :productModel="productResponse.model"
            :scores="productResponse.scores"
          />
        </SeoContent>
      </RevContainer>

      <BlockSection
        v-if="cmsSections"
        class="mb-56"
        :section="cmsSections.section5"
      />

      <RevContainer>
        <Articles v-if="productResponse" :sourceUrl="productPageUrl" />
        <AboutUs />
        <TrustList class="md:my-40" data-qa="product-reassurance" />
      </RevContainer>
    </div>

    <ClientOnly v-if="productResponse">
      <SwapModalWrapper
        v-if="productResponse.isTradeInEligible"
        :initialPayload="swapModalStore.initialPayload"
        :modal-name="BUYBACK_MODAL_NAMES.SWAP_MIX_AND_MATCH"
        :price="discountedPrice"
        zone="swap_pp_block"
      />

      <SwapModalWrapper
        v-if="productResponse.isTradeInEligible"
        :image="productResponse.images?.[0]"
        intro="atc"
        :modal-name="BUYBACK_MODAL_NAMES.SWAP_ATC"
        :price="discountedPrice"
        :price-without-trade-in
        :product-title="productResponse.model"
        :redirect-on-close-target="CHECKOUT.CART"
        zone="swap_pp_atc"
        @continue="handleContinue"
      />

      <TradeInModal v-if="showTradeInModal" />

      <VisibleByVerizonDrawer
        v-if="
          hasPartnerPromoCode(
            productResponse.includedServiceOffers.partnerPromoCodes,
            'VISIBLE',
          ) && visiblePartnerPromoCode
        "
        :promo-code="visiblePartnerPromoCode"
      />

      <template v-if="selectedOffer && selectedOffer.offerLegacyId">
        <AddToCartOverlay
          :items="[
            // Current product page item
            {
              listingId: selectedOffer.offerLegacyId,
              image: productResponse.images[0].url,
              title: productResponse.titles.raw,
              quantity: 1,
            },
            // If user added a bundle to cart
            ...(!!additionalProduct
              ? [
                  {
                    listingId: parseInt(additionalProduct.listingId, 10),
                    image: additionalProduct.image,
                    title: `${additionalProduct.title} ${additionalProduct.description}`,
                    quantity: 1,
                  },
                ]
              : []),
          ]"
          :opened="shouldShowAddToCartOverlay"
          :trade-in="buybackOffer?.product.fullTitle ?? null"
          @close="onAddToCartOverlayClose"
        />
      </template>

      <StickyBarBottom
        v-if="enableStickyBarDisplay"
        :price="price!"
        :price-without-subsidies
        :product="productResponse"
        :selected-mobile-plan
        :selected-offer="selectedOffer!"
        :show-bar="!isAddToCartButtonMobileInViewport"
        :tracking="productTracking"
      />
      <FlashSalesModal
        v-if="flashSalesDiscount && shouldDisplayFlashSales"
        :category-id="productResponse.tracking.categoryId"
        :discount="flashSalesDiscount"
      />
      <SellerFlashSalesModal
        v-else-if="productResponse.hasDeals"
        :product-id="productResponse.productId"
        :product-name="productResponse.titles.raw"
      />
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import { useRoute, useRouter, useRuntimeConfig } from '#imports'
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/composables/buybackOffer/useBuybackOffer'
import BlockSection from '@backmarket/nuxt-layer-cms/BlockSection.vue'
import FlashSalesModal from '@backmarket/nuxt-layer-recommendation/FlashSalesModal.vue'
import SellerFlashSalesModal from '@backmarket/nuxt-layer-recommendation/SellerFlashSalesModal.vue'
import { logDesync } from '@backmarket/nuxt-layer-recommendation/helpers.ts'
import type { Product } from '@backmarket/nuxt-layer-recommendation/models/product'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import {
  useAddCacheTagHeader,
  useAddServiceCacheTagHeader,
} from '@backmarket/nuxt-module-http-cache/composables/useAddCacheTagHeader'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { toNuxtLink } from '@backmarket/nuxt-module-navigation/toNuxtLink'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { Breakpoint } from '@backmarket/utils/dom/getCurrentBreakpoint'
import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'
import { RevContainer } from '@ds/components/Container'
import { RevDivider } from '@ds/components/Divider'
import { useElementVisibility } from '@vueuse/core'

import TheMainContent from '~/components/TheMainContent/TheMainContent.vue'
import SkinnyBanner from '~/scopes/branding/components/SkinnyBanner/SkinnyBanner.vue'
import { MODAL_NAMES as BUYBACK_MODAL_NAMES } from '~/scopes/buyback/constants'
import SwapModalWrapper from '~/scopes/buyback/swap/components/SwapModalWrapper/SwapModalWrapper.vue'
import { useSwapModalStore } from '~/scopes/buyback/swap/stores/useSwapModalStore'
import AddToCartOverlay from '~/scopes/checkout/components/AddToCartOverlay/AddToCartOverlay.vue'
import { CHECKOUT } from '~/scopes/checkout/routes-names'
import { useAddSectionsCacheTagHeader } from '~/scopes/cms/composables/useSetCMSPageCacheTagHeader'
import Breadcrumb from '~/scopes/navigation/components/Breadcrumb/Breadcrumb.vue'
import Articles from '~/scopes/product/components/Articles/Articles.vue'
import BreadcrumbProductPage from '~/scopes/product/components/Breadcrumb/Breadcrumb.vue'
import TrustList from '~/scopes/reassurance/components/TrustList/TrustList.vue'
import ReviewsBlock from '~/scopes/reviews/reviews-display/components/ReviewsBlock/ReviewsBlock.vue'

import AboutUs from '../components/AboutUs/AboutUs.vue'
import AdvertisedPayment from '../components/AdvertisedPayment/AdvertisedPayment.vue'
import CheckpointsCarousel from '../components/CheckpointsCarousel/CheckpointsCarousel.vue'
import Container from '../components/Container/Container.vue'
import Seller from '../components/CustomizationFunnel/components/Proposal/components/Seller/Seller.vue'
import VisibleByVerizonDrawer from '../components/CustomizationFunnel/components/VisibleByVerizonDrawer/VisibleByVerizonDrawer.vue'
import CustomizationFunnelWrapper from '../components/CustomizationFunnelWrapper/CustomizationFunnelWrapper.vue'
import FlashSalesSticker from '../components/FlashSalesSticker/FlashSalesSticker.vue'
import GalleryNew from '../components/GalleryNew/GalleryNew.vue'
import OutOfStock from '../components/OutOfStock/OutOfStock.vue'
import ReassuranceItems from '../components/ReassuranceItems/ReassuranceItems.vue'
import Recommendations from '../components/Recommendations/Recommendations.vue'
import ScoreCard from '../components/ScoreCard/ScoreCard.vue'
import SellerComment from '../components/SellerComment/SellerComment.vue'
import SeoContent from '../components/SeoContent/SeoContent.vue'
import SeoLinks from '../components/SeoLinks/SeoLinks.vue'
import StickyBarBottom from '../components/StickyBarBottom/StickyBarBottom.vue'
import TitleDesktop from '../components/Title/TitleDesktop.vue'
import TitleMobile from '../components/Title/TitleMobile.vue'
import TitleMobileAtc from '../components/Title/TitleMobileAtc.vue'
import TradeInModal from '../components/TradeIn/components/TradeInModal/TradeInModal.vue'
import VerifiedRefurbishedCarousel from '../components/VerifiedRefurbishedCarousel/VerifiedRefurbishedCarousel.vue'
import { useAddGradeInUrl } from '../composables/useAddGradeInUrl'
import { useGradeFallback } from '../composables/useGradeFallback'
import { useInvalidGradeFallback } from '../composables/useInvalidGradeFallback'
import { useOpenAddToCartOverlay } from '../composables/useOpenAddToCartOverlay'
import { useProductHead } from '../composables/useProductHead/useProductHead'
import { useProductPageSeoLinks } from '../composables/useProductPageSeoLinks'
import { useProductPrice } from '../composables/useProductPrice'
import { useProductRematch } from '../composables/useProductRematch'
import { useProductRequests } from '../composables/useProductRequests'
import { useProductTracking } from '../composables/useProductTracking'
import { useRedirectionToQueryParamsURL } from '../composables/useRedirectionToQueryParamsURL'
import { useUrlParams } from '../composables/useUrlParams'
import { EXPRESS_REPLACEMENT_CATEGORIES } from '../constants'
import { PRODUCT } from '../route-names'
import { hasPartnerPromoCode } from '../utils/hasPartnerPromoCode'

const route = useRoute()
const router = useRouter()
const marketPlace = useMarketplace()
const experiments = useExperiments()

const {
  FF_PRODUCT_PAGE_TRADE_IN_CHIP_JP = '',
  FF_PRODUCT_PAGE_TRADE_IN_CHIP_AU = '',
} = useRuntimeConfig().public
const i18n = useI18n()

const { grade: urlGrade, offerType, productId, mobilePlan } = useUrlParams()

const additionalProduct = ref<Product | null>(null)
const outOfStockAlternativeLoading = ref(true)
const outOfStockAlternative = ref<Product | null>(null)
const errorReco = ref(false)

const {
  shouldShowAddToCartOverlay,
  closeAddToCartOverlay,
  openAddToCartOverlay,
} = useOpenAddToCartOverlay()
// This is used to check if the add to cart button is in the viewport
// on mobile to hide the sticky bar bottom when it is
const addToCartMobileButtonVisibilityBeacon = useTemplateRef<HTMLDivElement>(
  'addToCartMobileButtonVisibilityBeacon',
)
const isAddToCartButtonMobileInViewport = useElementVisibility(
  addToCartMobileButtonVisibilityBeacon,
)

const {
  productResponse,
  breadcrumbResponse,
  technicalSpecificationsResponse,
  pickersResponse,
  servicesPickersResponse,
  pickersResponseCacheTagHeader,
  isPickersResponsePending,
  isServicesPickersResponsePending,
  ratesResponse,
  reviewsResponse,
  isReviewsResponsePending,
  isOutOfStock,
  selectedOffer,
  callServicesPickers,
  cmsSections,
  reviewsSummaryPending,
  reviewsSummaryResponse,
  reviewInCarouselResponse,
  reviewsAiSummaryPending,
  reviewsAISummary,
} = await useProductRequests(productId, urlGrade, offerType, mobilePlan)

// Set cache Header for Cloudflare
useAddCacheTagHeader(pickersResponseCacheTagHeader)

useAddServiceCacheTagHeader()

useAddSectionsCacheTagHeader(cmsSections.value?.id)

const shouldDisplayNewVerifiedRefurbishedCarousel =
  experiments['experiment.ppVerifiedRefurbishedCarouselV2'] === 'withNewDesign'

const shouldDisplayFlashSales =
  experiments['experiment.flashSales'] === 'enabled'

// <!-- // TODO by category -->
const showExpressReplacement = computed(() => {
  return (
    experiments['experiment.ppExpressReplacement'] ===
      'withExpressReplacement' &&
    marketPlace.market.countryCode === MarketCountryCode.FR &&
    productResponse.value?.tracking.categoryId !== undefined &&
    EXPRESS_REPLACEMENT_CATEGORIES.includes(
      productResponse.value?.tracking.categoryId,
    )
  )
})

const productPageUrl = computed(() => {
  if (!productResponse.value) {
    return ''
  }

  return router.resolve(
    toNuxtLink({
      name: PRODUCT.HOME,
      params: {
        locale: toBcp47Locale(i18n.currentLocale),
        slugV2: productResponse.value?.seo.slug,
        uuid: productResponse.value?.productId,
      },
    }),
  ).href
})

const swapModalStore = useSwapModalStore()

const showTradeInModal = computed(() => {
  return (
    FF_PRODUCT_PAGE_TRADE_IN_CHIP_AU.includes(marketPlace.market.countryCode) ||
    FF_PRODUCT_PAGE_TRADE_IN_CHIP_JP.includes(marketPlace.market.countryCode) ||
    productResponse.value?.isTradeInEligible
  )
})

const reviews = computed(() => ({
  isPending: isReviewsResponsePending.value,
  list: reviewsResponse.value ? reviewsResponse.value.results : [],
}))

const seoLinksList = useProductPageSeoLinks(productPageUrl.value)

const selectedMobilePlan = computed(() => {
  return (
    servicesPickersResponse.value?.pickerGroups
      ?.find(({ id }) => id === 'mobile_plan')
      ?.items?.find(({ selected }) => !!selected)?.mobilePlanOffer ?? undefined
  )
})

const visiblePartnerPromoCode = computed(() => {
  return productResponse.value?.includedServiceOffers.partnerPromoCodes.find(
    (partnerPromoCode) => partnerPromoCode.partnerName === 'VISIBLE',
  )
})

const hasNoVariants = computed(() => {
  return pickersResponse.value?.pickerGroups.length === 0
})

const flashSalesDiscount = computed(() => {
  return productResponse.value?.promoCodes.find(
    (promoCode) => promoCode.isFlashSale,
  )?.discountValue
})

useProductRematch(productResponse)
useInvalidGradeFallback()
useGradeFallback(pickersResponse)
useAddGradeInUrl(selectedOffer)
useRedirectionToQueryParamsURL()
useProductHead(
  productResponse,
  selectedOffer,
  ratesResponse,
  reviewsResponse,
  technicalSpecificationsResponse,
  isOutOfStock,
)
const productTracking = useProductTracking(
  productResponse,
  selectedOffer,
  ratesResponse,
  pickersResponse,
  isPickersResponsePending,
  servicesPickersResponse,
  isServicesPickersResponsePending,
)
const { buybackOffer, getDiscountedPrice } = useBuybackOffer()
const { price, priceWithoutSubsidies, priceWithoutTradeIn } = useProductPrice({
  pickers: servicesPickersResponse,
  selectedOffer,
})

const discountedPrice = computed(() =>
  priceWithoutSubsidies.value
    ? getDiscountedPrice(priceWithoutSubsidies.value)
    : undefined,
)

const breakpoint = useClientOnlyComputedBreakpoint()
const isMobile = computed(() => breakpoint.value < Breakpoint.MD)

function onAddToCartOverlayClose() {
  closeAddToCartOverlay()
  additionalProduct.value = null
}
function onBundleAddToCart(_listings: Array<string>, product: Product | null) {
  additionalProduct.value = product
  openAddToCartOverlay()
}

function onRecommendationsLoaded(products: Array<Product>) {
  outOfStockAlternative.value = products[0]
  outOfStockAlternativeLoading.value = false
}

function onRecommendationsError() {
  errorReco.value = true
  outOfStockAlternativeLoading.value = false
}

function handleContinue() {
  setTimeout(() => {
    openAddToCartOverlay()
  })
}

onMounted(() => {
  if (productResponse.value?.productId) {
    logDesync({
      id: productResponse.value.productId,
      isOutOfStock: isOutOfStock.value,
      price: selectedOffer.value?.price?.amount,
    })
  }
})

onBeforeUnmount(() => {
  closeAddToCartOverlay()
})

watch([buybackOffer], () => {
  callServicesPickers()
})

const enableStickyBarDisplay = computed(() => {
  return Boolean(
    !isOutOfStock.value &&
      productResponse.value &&
      selectedOffer.value &&
      price.value,
  )
})
</script>

<style>
/* On mobile, we need to add some padding to be able to see everything in the footer due to the sticky add to cart bar */
@media only screen and (max-width: 767px) {
  #main-footer {
    padding-bottom: 100px !important;
  }
}
</style>
